import React, { useEffect, useState, ReactElement, useRef } from 'react';
import PropTypes from 'prop-types';
import Rheostat from 'rheostat';
import MediaQuery from 'react-responsive';
import { remove, includes } from 'lodash';
import styled from '@emotion/styled';

import FiltersLivingIn from './FiltersLivingIn';
import Container from '../../blocks/Container';
import Checkbox from '../../../atoms/Checkbox';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import TextInput from '../../../atoms/TextInput';
import FilterSection from '../../blocks/FilterSection';
import UserConfig from '../../../config/User';
import { extractLocationData, metricConv } from '../../../common';
import { getBackgroundTheme, getButtonTheme, getRheostatTheme } from '../../../style';
import { isDarkMode } from '../../../utils/helpers';
import { auth10tixVerification } from '../../../config/Master';
import DefaultSearch from '../../../config/Search';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import { searchResetFilters, searchApplyFilters } from '../../../actions/searchTrackAction';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import breakpoints from '../../../theme/material/breakpoints';
import { CustomStyledProps } from 'style/style';
import { ToggleV2 } from 'swui';
import VerifiedBadgeIcon from 'atoms/icons/VerifiedBadgeIcon';
import { FONT_FAMILIES } from 'theme/material/constants';

// eslint-disable-next-line no-empty-pattern
const FilterSectionContainer = styled('div')(({}) => ({
  backgroundColor: getBackgroundTheme().block,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  zIndex: 996,
}));

// eslint-disable-next-line no-empty-pattern
const BottomSectionContainer = styled('div')(({}) => ({
  position: 'sticky',
  padding: '30px 0',
  bottom: 0,
  backgroundColor: getBackgroundTheme().block,
  width: '100%',
  borderTop: '1px solid rgba(0, 0, 0, 0.14)',
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const MobileApplyFiltersButton = styled(Button)`
  ${({ hideButton }: CustomStyledProps) => hideButton && 'display: none'}
`;

const FilterSpacer = styled('div')({
  paddingTop: 90,

  [breakpoints.down(639.05)]: {
    paddingTop: 20,
  },
  [breakpoints.down(380)]: {
    paddingTop: 50,
  },
});

const generousProfile = (gender, accountType, lookingFemale, lookingMale) =>
  gender === 'Male' && accountType === 'Generous' && lookingFemale && !lookingMale;

const setAgeFilter = (gender, accountType, lookingFemale, lookingMale, setSearchFilters) => {
  if (generousProfile(gender, accountType, lookingFemale, lookingMale)) {
    return setSearchFilters.age_max < UserConfig.maximumAgeMaleForFemale
      ? setSearchFilters.age_min + '-' + setSearchFilters.age_max
      : setSearchFilters.age_min + '+';
  } else if (setSearchFilters.age_max < UserConfig.maximumAgeFemaleForMale) {
    return setSearchFilters.age_min + '-' + setSearchFilters.age_max;
  } else {
    return setSearchFilters.age_min + '+';
  }
};

const setRheostatDarkMode = rheostat => {
  const themeDarkMode = isDarkMode();

  if (themeDarkMode) {
    rheostat[0].style.backgroundColor = getRheostatTheme().bgColor;
    rheostat[1].style.backgroundColor = getRheostatTheme().bgColor;
  }
};

const setAgeMax = (searchFilters, defaultMaxAge) =>
  searchFilters.age_max < defaultMaxAge ? searchFilters.age_max : defaultMaxAge;

const setLoc = place => {
  const emptyLocation = {
    city: '',
    country: '',
    latitude: '',
    longitude: '',
    region: '',
  };

  // apply location to state
  return !place ? emptyLocation : extractLocationData(place);
};

interface FiltersProps {
  gender: any;
  accountType: any;
  lookingMale: any;
  lookingFemale: any;
  searchFilters: any;
  resetFiltersState: any;
  resetFilters: any;
  screenSize?: any;
  applyFiltersHandler: any;
  resetIndividualFilter?: any;
  saveLocation?: any;
  darkmode?: boolean;
}

const customMobileStyles = {
  width: '46%',
  margin: '0',
};

const RheostatSlider = styled(Rheostat)(
  () => `
  .rheostat-progress {
    background-color: ${getRheostatTheme().bgColor} !important;
  }
`
);

const StyledToggle = styled(ToggleV2)({
  height: '28px !important',
  width: '56px !important',
});

const iconStyle = {
  verticalAlign: 'middle',
  width: '20px',
  height: '20px',
  marginTop: '-3px',
  marginRight: 3,
};

const filterGroupStyle = {
  display: 'flex',
  margin: '0',
  padding: '20px 0px',
};

const filterHeaderStyle = {
  display: 'flex',
  [breakpoints.down(640)]: {
    padding: 0,
    width: '50%',
  },
};

const filterRightStyle = {
  margin: 0,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  [breakpoints.down(640)]: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '50%',
    margin: 0,
  },
};

const FilterButtonSpan = styled('span')({
  fontSize: 18,
  letterSpacing: -0.32,
  lineHeight: '16px',
  fontWeight: 700,
  fontFamily: FONT_FAMILIES.primary,
  [breakpoints.down(768.05)]: {
    fontSize: 14,
    fontWeight: 600,
  },
});

const Filters = ({
  gender,
  accountType,
  lookingMale,
  lookingFemale,
  searchFilters,
  resetFiltersState,
  resetFilters,
  screenSize,
  applyFiltersHandler,
}: FiltersProps): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const defaultMaxAge = generousProfile(gender, accountType, lookingFemale, lookingMale)
    ? UserConfig.maximumAgeMaleForFemale
    : UserConfig.maximumAgeFemaleForMale;
  const [mySearchFilters, setSearchFilters] = useState({
    ...searchFilters,
    age_max: setAgeMax(searchFilters, defaultMaxAge),
  });
  const [hiddenMobileApplyFilterButton, setHiddenMobileApplyFilter] = useState(false);
  const bodyTypes = mySearchFilters.body_types !== undefined ? 'body_types' : 'body_type';
  const sameSearchFilters = mySearchFilters === searchFilters;

  const resetFilterBtn = useRef(null);

  useOnClickTriggerTracking('Reset Filters', resetFilterBtn, searchResetFilters);

  const setHiddenMobileApplyFilterButton = status => {
    // true = hidden, false = visible.
    setHiddenMobileApplyFilter(status);
  };

  useEffect(() => {
    // do componentDidMount logic
    const rheostat = document.getElementsByClassName('rheostat-progress');

    setRheostatDarkMode(rheostat);
  }, []);

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  useEffect(() => {
    if (sameSearchFilters) return false;
    // receiving new search filters from redux
    setSearchFilters({
      ...searchFilters,
      age_max: setAgeMax(searchFilters, defaultMaxAge),
    });
  }, [searchFilters, defaultMaxAge, sameSearchFilters]);

  const onResetFilterHandler = () => {
    setSearchFilters({
      ...DefaultSearch.filters,
      age_max: setAgeMax(searchFilters, defaultMaxAge),
    });
    resetFiltersState();
    resetFilters();
    resetFilterBtn?.current?.blur();
  };

  const onChangeHandler = e => {
    const newSearchFilterVal = {};

    newSearchFilterVal[e.target.id] = e.target.value;
    setSearchFilters({ ...mySearchFilters, ...newSearchFilterVal });
  };

  const onChangeBoolean = e => {
    const newSearchFilterVal = {};

    newSearchFilterVal[e.target.id] = !mySearchFilters[e.target.id];
    setSearchFilters({ ...mySearchFilters, ...newSearchFilterVal });
  };

  const onChangeToggle = e => {
    const toggleVal = e.target.checked ? 2 : 1;

    setSearchFilters(prev => ({
      ...prev,
      [e.target.id]: toggleVal == 1 ? [] : [toggleVal],
    }));
  };

  const onChangeAge = (age_min, age_max) => {
    const newSearchFilterVal = { age_min: age_min, age_max: age_max };

    setSearchFilters({ ...mySearchFilters, ...newSearchFilterVal });
  };

  const onChangeHeight = (height_min, height_max) => {
    const newSearchFilterVal = { height_min: height_min, height_max: height_max };

    setSearchFilters({ ...mySearchFilters, ...newSearchFilterVal });
  };

  const onCheckBoxChange = (attr, theVal) => {
    // assign create arrays for each checkbox input block
    if (isCheckBoxChecked(attr, theVal)) {
      setSearchFilters({
        ...mySearchFilters,
        [attr]: remove(mySearchFilters[attr], n => {
          return n !== theVal;
        }),
      });
    } else {
      // add item to array
      const newState = mySearchFilters && mySearchFilters[attr] ? mySearchFilters[attr].slice() : [];

      newState.push(theVal);
      setSearchFilters({ ...mySearchFilters, [attr]: newState });
    }
  };

  const isCheckBoxChecked = (attr, theVal) => {
    // returns value of Checkbox item
    const theArray = mySearchFilters[attr];

    return includes(theArray, theVal);
  };

  const saveLocation = place => {
    const loc = setLoc(place);

    setSearchFilters({ ...mySearchFilters, ...loc });
  };

  return (
    <FilterSectionContainer>
      <Container style={{ maxWidth: 1400, padding: '0 15px' }}>
        <div>{screenSize}</div>
        <FiltersLivingIn
          theState={mySearchFilters}
          onChangeBoolean={onChangeBoolean}
          onChangeHandler={onChangeHandler}
          saveLocation={saveLocation}
          setHiddenMobileApplyFilterButton={setHiddenMobileApplyFilterButton}
          resetFiltersState={onResetFilterHandler}
        />
        {auth10tixVerification && (
          <FilterSection
            fullWidth
            collapsable={false}
            header="ID Verified"
            icon={<VerifiedBadgeIcon verifiedColor={'#60ABE1'} style={iconStyle} />}
            filterGroupStyle={filterGroupStyle}
            filterHeaderStyle={filterHeaderStyle}
            filterRightStyle={filterRightStyle}
          >
            <StyledToggle
              className="sidebarToggle"
              id="id_verified"
              checked={mySearchFilters?.id_verified?.includes(2)}
              onChange={onChangeToggle}
            />
          </FilterSection>
        )}
        <FilterSection
          fullWidth
          collapsable={false}
          header="Selfie Verified"
          icon={<VerifiedBadgeIcon verifiedColor={'#E2C92D'} style={iconStyle} />}
          filterGroupStyle={filterGroupStyle}
          filterHeaderStyle={filterHeaderStyle}
          filterRightStyle={filterRightStyle}
        >
          <StyledToggle
            className="sidebarToggle"
            id="selfie_verified"
            checked={mySearchFilters?.selfie_verified?.includes(2)}
            onChange={onChangeToggle}
          />
        </FilterSection>

        <FilterSection
          header="Looking for"
          numActive={[
            searchFilters.dating_just_dating,
            searchFilters.dating_friendship,
            searchFilters.dating_marriage,
            searchFilters.dating_sugar,
            searchFilters.dating_married,
            searchFilters.dating_adult,
            searchFilters.dating_mentorship,
          ].reduce(function (a, b) {
            return b ? a + 1 : a;
          }, 0)}
        >
          <Checkbox
            size="MD"
            label="Dating"
            value={mySearchFilters.dating_just_dating}
            onChange={onChangeBoolean}
            id="dating_just_dating"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            label="Friendship"
            value={mySearchFilters.dating_friendship}
            onChange={onChangeBoolean}
            id="dating_friendship"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            label="Long-term"
            value={mySearchFilters.dating_marriage}
            onChange={onChangeBoolean}
            id="dating_marriage"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            label="Discreet affair"
            value={mySearchFilters.dating_married}
            onChange={onChangeBoolean}
            id="dating_married"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            label="Casual"
            value={mySearchFilters.dating_adult}
            onChange={onChangeBoolean}
            id="dating_adult"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            label="Mentorship"
            value={mySearchFilters.dating_mentorship}
            onChange={onChangeBoolean}
            id="dating_mentorship"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
        </FilterSection>

        <FilterSection
          fullWidth
          header="Age"
          hideSelectedText
          numActive={setAgeFilter(gender, accountType, lookingFemale, lookingMale, mySearchFilters)}
        >
          <div style={{ fontSize: 15, lineHeight: '20px', letterSpacing: -0.5, marginBottom: 22 }}>
            {setAgeFilter(gender, accountType, lookingFemale, lookingMale, mySearchFilters)}
          </div>

          <RheostatSlider
            min={UserConfig.minimumAge}
            max={
              generousProfile(gender, accountType, lookingFemale, lookingMale)
                ? UserConfig.maximumAgeMaleForFemale
                : UserConfig.maximumAgeFemaleForMale
            }
            values={[mySearchFilters.age_min, mySearchFilters.age_max]}
            snap={true}
            onChange={e => {
              onChangeAge(e.values[0], e.values[1]);
            }}
            onValuesUpdated={e => {
              onChangeAge(e.values[0], e.values[1]);
            }}
          />
        </FilterSection>
        <FilterSection
          fullWidth
          header="Height"
          numActive={
            UserConfig.minimumHeight !== mySearchFilters.height_min ||
            UserConfig.maximumHeight !== mySearchFilters.height_max
              ? `${metricConv(mySearchFilters.height_min)} – ${metricConv(mySearchFilters.height_max)}`
              : undefined
          }
          hideSelectedText
        >
          <div style={{ fontSize: 15, lineHeight: '20px', letterSpacing: -0.5, marginBottom: 22 }}>
            {metricConv(mySearchFilters.height_min)} – {metricConv(mySearchFilters.height_max)}
          </div>
          <RheostatSlider
            min={UserConfig.minimumHeight}
            max={UserConfig.maximumHeight}
            values={[mySearchFilters.height_min, mySearchFilters.height_max]}
            snap={true}
            onChange={e => {
              onChangeHeight(e.values[0], e.values[1]);
            }}
            onValuesUpdated={e => {
              onChangeHeight(e.values[0], e.values[1]);
            }}
          />
        </FilterSection>
        <FilterSection header="Smoking" numActive={mySearchFilters.smoking.length}>
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('smoking', 1)}
            onChange={() => {
              onCheckBoxChange('smoking', 1);
            }}
            label="No"
            id="smoking_no"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('smoking', 2)}
            onChange={() => {
              onCheckBoxChange('smoking', 2);
            }}
            label="Yes"
            id="smoking_yes"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('smoking', 3)}
            onChange={() => {
              onCheckBoxChange('smoking', 3);
            }}
            label="Occasionally"
            id="smoking_occasionally"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
        </FilterSection>
        <FilterSection header="Drinking" numActive={mySearchFilters.drinking.length}>
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('drinking', 1)}
            onChange={() => {
              onCheckBoxChange('drinking', 1);
            }}
            label="No"
            id="drinking_no"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('drinking', 2)}
            onChange={() => {
              onCheckBoxChange('drinking', 2);
            }}
            label="Yes"
            id="drinking_yes"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('drinking', 3)}
            onChange={() => {
              onCheckBoxChange('drinking', 3);
            }}
            label="Occasionally"
            id="drinking_occasionally"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
        </FilterSection>
        <FilterSection header="Children" numActive={mySearchFilters.children.length}>
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('children', 1)}
            onChange={() => {
              onCheckBoxChange('children', 1);
            }}
            label="No"
            id="children_no"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('children', 2)}
            onChange={() => {
              onCheckBoxChange('children', 2);
            }}
            label="Yes"
            id="children_yes"
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
        </FilterSection>
        <FilterSection
          header="Body types"
          numActive={
            mySearchFilters.body_types !== undefined
              ? mySearchFilters.body_types.length
              : mySearchFilters.body_type.length
          }
        >
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 5)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 5);
            }}
            label="A few extra pounds"
            id="body_type_extra_pounds"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 2)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 2);
            }}
            label="Athletic"
            id="body_type_athletic"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 3)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 3);
            }}
            label="Average"
            id="body_type_average"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 4)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 4);
            }}
            label="Curvy"
            id="body_type_curvy"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 6)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 6);
            }}
            label="Full-figured"
            id="body_type_full_figured"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 7)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 7);
            }}
            label="Muscular"
            id="body_type_muscular"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked(bodyTypes, 1)}
            onChange={() => {
              onCheckBoxChange(bodyTypes, 1);
            }}
            label="Slender"
            id="body_type_slender"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
        </FilterSection>
        <FilterSection header="Education" numActive={mySearchFilters.education.length}>
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('education', 1)}
            onChange={() => {
              onCheckBoxChange('education', 1);
            }}
            label="High school"
            id="education_high_school"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('education', 2)}
            onChange={() => {
              onCheckBoxChange('education', 2);
            }}
            label="Some college"
            id="education_some_college"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('education', 3)}
            onChange={() => {
              onCheckBoxChange('education', 3);
            }}
            label="Associates degree"
            id="education_associates_degree"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('education', 4)}
            onChange={() => {
              onCheckBoxChange('education', 4);
            }}
            label="Bachelor's degree"
            id="education_bachelors_degree"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('education', 5)}
            onChange={() => {
              onCheckBoxChange('education', 5);
            }}
            label="Graduate degree"
            id="education_graduate_degree"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
          <Checkbox
            size="MD"
            value={isCheckBoxChecked('education', 6)}
            onChange={() => {
              onCheckBoxChange('education', 6);
            }}
            label="Post-doctoral degree"
            id="education_post_doctoral"
            labelCustomStyle={{ display: 'flex' }}
            customStyle={{ flexShrink: 0 }}
            customLabelTextStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: '-0.3px' }}
          />
        </FilterSection>
        <FilterSection header="Profile Keywords" collapsable={false} fullWidth>
          <TextInput
            className="textInput"
            type="text"
            id="keywords"
            name="keywords"
            onChange={onChangeHandler}
            value={mySearchFilters.keywords}
            customStyle={{ paddingBottom: '4px', width: isMobile ? '100%' : 'unset' }}
          />
        </FilterSection>
        <FilterSpacer>{/* spacer to push bottom filter above fixed filter bar */}</FilterSpacer>
      </Container>
      <BottomSectionContainer
        style={{
          zIndex: isMobile ? 996 : 2,
        }}
      >
        <Container
          style={{
            paddingTop: isMobile ? 13 : 'unset',
            maxWidth: 1400,
            padding: '0 15px',
          }}
        >
          <MediaQuery minWidth={640}>
            <ButtonSet align="right">
              <Button
                buttonRef={resetFilterBtn}
                customStyle={{ color: getButtonTheme().darkReverse }}
                onClick={onResetFilterHandler}
                buttonType="secondaryRed"
              >
                <FilterButtonSpan>Reset Filter</FilterButtonSpan>
              </Button>
              <Button
                buttonType="primaryRed"
                onClick={() => {
                  applyFiltersHandler(mySearchFilters);
                  searchApplyFilters(mySearchFilters);
                }}
              >
                <FilterButtonSpan>Apply Filters</FilterButtonSpan>
              </Button>
            </ButtonSet>
          </MediaQuery>
          <MediaQuery maxWidth={640} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              buttonRef={resetFilterBtn}
              customStyle={customMobileStyles}
              onClick={onResetFilterHandler}
              buttonType={'secondaryRed'}
            >
              <FilterButtonSpan>Reset Filter</FilterButtonSpan>
            </Button>
            <MobileApplyFiltersButton
              customStyle={customMobileStyles}
              buttonType="primaryRed"
              disabled={hiddenMobileApplyFilterButton}
              onClick={() => {
                applyFiltersHandler(mySearchFilters);
                searchApplyFilters(mySearchFilters);
              }}
            >
              <FilterButtonSpan>View Results</FilterButtonSpan>
            </MobileApplyFiltersButton>
          </MediaQuery>
        </Container>
      </BottomSectionContainer>
    </FilterSectionContainer>
  );
};

Filters.propTypes = {
  filterClickHandler: PropTypes.func.isRequired,
};
export default Filters;
